import { positionKey } from "../chess/positionKey.ts";
import { teachingMoveFor } from "../lessons/repository.ts";
import type { LessonCourse } from "../lessons/schema.ts";

/** Drop sentences that name the move, so a prompt can ask without answering. */
export function textWithoutSan(text: string, san: string | null | undefined): string {
  const source = text.trim();
  if (!source) return "";
  if (!san) return source;
  const bare = san.replace(/[+#?!]/g, "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const names = new RegExp(`\\b${bare}\\b`);
  const kept = source.split(/(?<=[.!?])\s+/).filter((sentence) => !names.test(sentence));
  return kept.join(" ").replace(/\s+/g, " ").trim();
}

/**
 * A position-specific prompt that does not contain the course move.
 * Prefers why, then the goal, then the piece, then the phase fallback.
 */
export function memoryCue(args: {
  goal?: string | null;
  why?: string | null;
  piece?: string | null;
  san?: string | null;
  fallback: string;
}): string {
  for (const source of [args.why, args.goal, args.piece]) {
    const cleaned = textWithoutSan(source || "", args.san);
    if (cleaned.length >= 20) return cleaned;
  }
  return args.fallback;
}

/** The first look at a side line: the idea, still without the move's name. */
export function ideaForPosition(args: {
  goal?: string | null;
  why?: string | null;
  san?: string | null;
  fallback: string;
}): string {
  const goal = textWithoutSan(args.goal || "", args.san);
  const why = textWithoutSan(args.why || "", args.san);
  const text = [goal, why && why !== goal ? why : ""].filter(Boolean).join(" ");
  return text.length >= 20 ? text : memoryCue({ ...args, fallback: args.fallback });
}

const bareSan = (san: string) => san.replace(/[+#?!]/g, "");

/**
 * From memory accepts the taught move and a transposition onto it.
 * Anything else is a miss, including a sound move the course does not play.
 */
export function recallAccepts(
  course: LessonCourse,
  fen: string,
  san: string
): { ok: boolean; expected: string | null } {
  const node = course.nodes[positionKey(fen)];
  const expected = teachingMoveFor(node)?.san || null;
  const edge = node?.moves.find((move) => bareSan(move.san) === bareSan(san));
  const role = edge?.role;
  const ok =
    role === "repertoire" ||
    role === "mainline" ||
    role === "transposition" ||
    Boolean(expected && bareSan(san) === bareSan(expected));
  return { ok, expected };
}
